import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Shield, 
  CheckCircle, 
  FileSearch, 
  BadgeCheck, 
  Award,
  Clock,
  Wrench,
  Users,
  Lock,
  ChevronRight,
  ArrowRight,
  Phone,
  Mail,
  Star,
  FileCheck,
  UserCheck,
  Scale
} from 'lucide-react';

const VettingProcessPage = () => {
  const vettingSteps = [
    {
      icon: FileSearch,
      title: "Application Review",
      description: "Every mechanic submits a detailed application including business credentials, certifications, and references.",
      details: [
        "Business license verification",
        "Insurance documentation review",
        "Professional certifications check",
        "Service offerings validation"
      ]
    },
    {
      icon: BadgeCheck,
      title: "Background Verification",
      description: "We conduct thorough background checks to ensure every mechanic meets our trust standards.",
      details: [
        "Business registration verification",
        "Years of experience validation",
        "Customer reference checks",
        "Online reputation review"
      ]
    },
    {
      icon: UserCheck,
      title: "Trust Commitment",
      description: "All mechanics must agree to our Trust Commitment pledge before joining the platform.",
      details: [
        "Honest pricing guarantee",
        "Transparent service recommendations",
        "No unnecessary upselling",
        "Clear communication commitment"
      ]
    },
    {
      icon: Scale,
      title: "Ongoing Monitoring",
      description: "We continuously monitor mechanic performance and customer feedback to maintain quality.",
      details: [
        "Customer review analysis",
        "Service quality audits",
        "Pricing transparency checks",
        "Response time monitoring"
      ]
    }
  ];

  const trustStats = [
    { value: "68%", label: "Application Rejection Rate", description: "Only the best get approved" },
    { value: "100%", label: "License Verified", description: "Every mechanic is licensed" },
    { value: "24hr", label: "Review Response", description: "Fast issue resolution" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center" data-testid="logo-link">
              <img src="/logo.png" alt="Mechanicease" className="h-14 w-auto" />
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              <Link to="/mechanics" className="text-slate-600 hover:text-slate-900 font-medium text-sm">
                Find Mechanics
              </Link>
              <Link to="/apply" className="text-slate-600 hover:text-slate-900 font-medium text-sm">
                Partner Program
              </Link>
              <Button className="bg-slate-900 hover:bg-slate-800 text-white text-sm px-5" asChild>
                <Link to="/register">Get Started</Link>
              </Button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-slate-900 py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, #f97316 0%, transparent 50%), radial-gradient(circle at 75% 75%, #f97316 0%, transparent 50%)'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2 mb-8">
              <Shield className="w-4 h-4 text-orange-500" />
              <span className="text-orange-500 text-sm font-medium">Our Commitment to Trust</span>
            </div>
            
            <h1 className="font-['Barlow'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="page-title">
              How We Vet Our Mechanics
            </h1>
            
            <p className="text-lg sm:text-xl text-slate-300 mb-10">
              Every mechanic on Mechanicease goes through a rigorous verification process. 
              We believe trust is earned, not given—which is why we reject 68% of applicants.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8" asChild>
                <Link to="/mechanics">Find Trusted Mechanics</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-slate-600 text-white hover:bg-slate-800" asChild>
                <Link to="/apply">Apply as a Mechanic</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Stats */}
      <section className="py-12 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {trustStats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`trust-stat-${index}`}>
                <p className="font-['Barlow'] text-4xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm font-semibold text-orange-500 uppercase tracking-wider mt-1">{stat.label}</p>
                <p className="text-sm text-slate-600 mt-1">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vetting Process Steps */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-['Barlow'] text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Our 4-Step Verification Process
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We've developed a comprehensive vetting process to ensure only the most trustworthy 
              mechanics join our platform. Here's how it works:
            </p>
          </div>

          <div className="space-y-8">
            {vettingSteps.map((step, index) => (
              <Card key={index} className="bg-white border border-slate-200 overflow-hidden" data-testid={`vetting-step-${index}`}>
                <CardContent className="p-0">
                  <div className="flex flex-col lg:flex-row">
                    {/* Step Number */}
                    <div className="lg:w-24 bg-slate-900 flex items-center justify-center p-6 lg:p-0">
                      <span className="font-['Barlow'] text-3xl font-bold text-orange-500">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                    
                    {/* Content */}
                    <div className="flex-1 p-6 lg:p-8">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-orange-50 rounded-sm flex items-center justify-center flex-shrink-0">
                          <step.icon className="w-6 h-6 text-orange-500" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-['Barlow'] text-xl font-bold text-slate-900 mb-2">
                            {step.title}
                          </h3>
                          <p className="text-slate-600 mb-4">
                            {step.description}
                          </p>
                          <div className="grid sm:grid-cols-2 gap-2">
                            {step.details.map((detail, detailIdx) => (
                              <div key={detailIdx} className="flex items-center gap-2">
                                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                <span className="text-sm text-slate-700">{detail}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Commitment Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2 mb-6">
                <Lock className="w-4 h-4 text-orange-500" />
                <span className="text-orange-500 text-sm font-medium">Our Promise</span>
              </div>
              
              <h2 className="font-['Barlow'] text-3xl sm:text-4xl font-bold text-white mb-6">
                The Mechanicease Trust Commitment
              </h2>
              
              <p className="text-slate-300 text-lg mb-8">
                Every mechanic who joins Mechanicease agrees to uphold our strict standards of 
                honesty and transparency. This isn't just a checkbox—it's a commitment we take seriously.
              </p>

              <div className="space-y-4">
                {[
                  "Provide honest assessments of all vehicle issues",
                  "Never recommend unnecessary repairs or services",
                  "Always provide detailed, itemized invoices",
                  "Maintain transparent pricing with no hidden fees",
                  "Communicate clearly throughout the repair process"
                ].map((commitment, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-slate-200">{commitment}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-slate-800 rounded-lg p-8 border border-slate-700">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-16 h-16 bg-orange-500/10 rounded-sm flex items-center justify-center">
                  <FileCheck className="w-8 h-8 text-orange-500" />
                </div>
                <div>
                  <h3 className="font-['Barlow'] text-xl font-bold text-white">Detailed Invoicing</h3>
                  <p className="text-slate-400 text-sm">Full transparency on every repair</p>
                </div>
              </div>

              <div className="space-y-4 text-sm">
                <div className="flex justify-between items-center py-3 border-b border-slate-700">
                  <span className="text-slate-400">Labor Hours</span>
                  <span className="text-white font-medium">Itemized by task</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-700">
                  <span className="text-slate-400">Labor Rate</span>
                  <span className="text-white font-medium">Clear hourly pricing</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-700">
                  <span className="text-slate-400">Parts Cost</span>
                  <span className="text-white font-medium">Individual part pricing</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-slate-700">
                  <span className="text-slate-400">Receipts</span>
                  <span className="text-white font-medium">Attached for verification</span>
                </div>
                <div className="flex justify-between items-center py-3">
                  <span className="text-slate-400">Tax Breakdown</span>
                  <span className="text-white font-medium">Transparent calculation</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-['Barlow'] text-3xl sm:text-4xl font-bold text-slate-900 text-center mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {[
              {
                q: "How long does the vetting process take?",
                a: "Our vetting process typically takes 3-5 business days. We take the time to thoroughly verify each applicant to maintain our high standards."
              },
              {
                q: "What happens if a mechanic violates the trust commitment?",
                a: "We take violations seriously. Depending on the severity, mechanics may receive warnings, temporary suspension, or permanent removal from our platform. Customer complaints are investigated within 24 hours."
              },
              {
                q: "How can I report an issue with a mechanic?",
                a: "You can report issues directly through your dashboard after a booking, or contact our support team. All reports are reviewed by our trust and safety team."
              },
              {
                q: "Are all mechanics on Mechanicease licensed?",
                a: "Yes, 100% of mechanics on our platform have verified business licenses. We verify each license during the application process and conduct periodic re-verification."
              }
            ].map((faq, index) => (
              <Card key={index} className="bg-white border border-slate-200" data-testid={`faq-${index}`}>
                <CardContent className="p-6">
                  <h3 className="font-['Barlow'] text-lg font-bold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-50 border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Barlow'] text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Ready to Find a Trusted Mechanic?
          </h2>
          <p className="text-slate-600 text-lg mb-8">
            Browse our network of verified, trustworthy mechanics and book your next service with confidence.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white px-8" asChild>
              <Link to="/mechanics">
                Browse Mechanics <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="border-slate-300" asChild>
              <Link to="/apply">
                Apply to Join <ChevronRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 py-12 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="Mechanicease" className="h-10 w-auto" />
            </Link>
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/mechanics" className="hover:text-white transition-colors">Mechanics</Link>
              <Link to="/vetting" className="hover:text-white transition-colors">Trust Process</Link>
              <Link to="/apply" className="hover:text-white transition-colors">Partner Program</Link>
            </div>
            <p className="text-slate-500 text-sm">© 2024 Mechanicease. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VettingProcessPage;
